import { useId, useState } from "react";
import { Link } from "react-router";

import { Chip, ChipRow } from "../lib/Chip.tsx";
import { EmptyState } from "../lib/EmptyState.tsx";
import { InitialsTile } from "../lib/InitialsTile.tsx";
import { Tag } from "../lib/Tag.tsx";
import { TileRow } from "../lib/Tiles.tsx";
import { WorkThumb } from "../lib/WorkThumb.tsx";
import { AddContactDialog } from "./AddContactDialog.tsx";
import { CONTACTS_COPY } from "./contacts.constants.ts";
import type { ContactRow, TimelineView } from "./contacts.model.ts";
import type { NoteSubject } from "./useContactsController.ts";
import { useContactsController } from "./useContactsController.ts";
import "./gallery.css";
import "./contacts.css";

/**
 * Contacts & CRM — the counterparty column, read from the other end.
 *
 * TWO SECTIONS, AND THE SPLIT IS THE SCREEN'S ONE REAL CLAIM. A contact with an
 * ONDO id is a registry identity; a contact without one is text this gallery
 * typed on its own sale, checked against nothing. Rendering them alike would
 * assert an identity the registry has never established, so they are separated
 * with a heading and a sentence rather than a badge somebody can miss.
 *
 * Built from the same objects as every other list here — `TileRow`, `ChipRow`,
 * `InitialsTile`, `Tag`, and the `<button aria-expanded>` row the agents screen
 * uses. Nothing bespoke, because a contact is not a different KIND of thing
 * from an agent or a placement; it is the same rows read the other way round.
 *
 * It carries no title of its own: the portal header above it has one.
 */
export function Contacts({
  workBase,
  canNote,
  actingAs = null,
}: {
  workBase: string;
  /**
   * Editor on this gallery. `ondo.set_contact_note()` requires it.
   *
   * The contacts and their timelines still render for a viewer — reading who
   * the firm has dealt with is what the screen is for. What is withheld is
   * writing the firm's own note about somebody, which is the firm speaking.
   */
  canNote: boolean;
  /**
   * The workspace organisation's ONDO id, or null on a personal workspace.
   *
   * It names which of the READER'S OWN seats this list is being read as, so
   * the stamp can say which firm they were acting for. The server refuses an
   * id the caller holds no seat in, so this chooses between their firms and
   * cannot assert an identity — and no export permission is derived from it.
   */
  actingAs?: string | null;
}) {
  const {
    view,
    stamp,
    timeline,
    choose,
    search,
    setSearch,
    add,
    openAdd,
    closeAdd,
    cancelAdd,
    setAddDraft,
    submitAdd,
    removeAdded,
    open,
    close,
    saveNote,
    clearNote,
    saving,
    exportCsv,
    exporting,
    exportProblem,
  } = useContactsController(workBase, actingAs);
  const searchId = useId();

  if (view.kind === "loading") {
    return (
      <p className="gallery__note" role="status">
        {view.message}
      </p>
    );
  }

  if (view.kind === "error") {
    return (
      <p className="gallery__note gallery__note--error" role="alert">
        {view.message}
      </p>
    );
  }

  return (
    <>
      {/*
        THE READER STAMP, FIRST ON THE SCREEN. Every field in it came back from
        the server on the same read as the rows — the name and the ONDO id are
        resolved from the session, never from anything this browser holds — and
        the same three facts are printed inside anything exported.
      */}
      {stamp ? (
        <section className="gallery__stamp" aria-label="Reader stamp">
          <p className="gallery__stamp-who">{stamp.who}</p>
          <p className="gallery__stamp-when">{stamp.when}</p>
          <p className="gallery__stamp-note">{stamp.note}</p>
        </section>
      ) : null}

      <section className="gallery__lead">
        <p>
          <strong>{CONTACTS_COPY.lead}</strong> {CONTACTS_COPY.leadDetail}
        </p>
      </section>

      <TileRow tiles={view.tiles} />

      {view.empty ? (
        <EmptyState className="gallery__empty" lead={CONTACTS_COPY.emptyLead} detail={CONTACTS_COPY.emptyDetail}>
          {/* An empty firm is precisely who needs the button. */}
          {canNote ? (
            <button type="button" className="gallery__contact-add" onClick={openAdd}>
              {CONTACTS_COPY.addContact}
            </button>
          ) : null}
        </EmptyState>
      ) : (
        <>
          <div className="gallery__toolbar">
            <label className="gallery__contact-search">
              <span className="gallery__visually-hidden">
                {CONTACTS_COPY.searchLabel}
              </span>
              <input
                id={searchId}
                className="gallery__contact-search-input"
                type="search"
                value={search}
                placeholder={CONTACTS_COPY.searchPlaceholder}
                title={CONTACTS_COPY.searchNote}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            <ChipRow label={CONTACTS_COPY.filterLabel}>
              {view.filters.map((chip) => (
                <Chip
                  key={chip.key}
                  pressed={view.filter === chip.key}
                  onClick={() => choose(chip.key)}
                >
                  {chip.label}
                </Chip>
              ))}
            </ChipRow>
            {canNote ? (
              <button type="button" className="gallery__contact-add" onClick={openAdd}>
                {CONTACTS_COPY.addContact}
              </button>
            ) : null}
            <button
              type="button"
              className="gallery__export"
              onClick={exportCsv}
              disabled={exporting}
            >
              {exporting ? CONTACTS_COPY.exporting : CONTACTS_COPY.exportCsv}
            </button>
          </div>

          {/* A refused export released no file, which is what the reader needs
              told — and the server's own sentence says why. */}
          {exportProblem ? (
            <p className="gallery__note gallery__note--error" role="alert">
              {exportProblem}
            </p>
          ) : null}

          {view.filteredOut ? (
            <p className="gallery__note">{CONTACTS_COPY.emptyDetail}</p>
          ) : null}

          <Section
            title={CONTACTS_COPY.attachedTitle}
            note={CONTACTS_COPY.attachedNote}
            rows={view.attached}
            onOpen={open}
            onSaveNote={saveNote}
            onClearNote={clearNote}
            onRemoveAdded={removeAdded}
            saving={saving}
            canNote={canNote}
          />

          <Section
            title={CONTACTS_COPY.namedTitle}
            note={CONTACTS_COPY.namedNote}
            rows={view.named}
            onOpen={open}
            onSaveNote={saveNote}
            onClearNote={clearNote}
            onRemoveAdded={removeAdded}
            saving={saving}
            canNote={canNote}
            named
          />

          <p className="gallery__note">{CONTACTS_COPY.sharingNote}</p>
        </>
      )}

      <Timeline timeline={timeline} onClose={close} />

      {/* Mounted even when shut — see AddContactDialog's header. */}
      <AddContactDialog
        view={add}
        onSetDraft={setAddDraft}
        onSubmit={submitAdd}
        onCancel={cancelAdd}
        onClose={closeAdd}
      />
    </>
  );
}

/** One of the two classes of contact, with the sentence that keeps them apart. */
function Section({
  title,
  note,
  rows,
  onOpen,
  onSaveNote,
  onClearNote,
  onRemoveAdded,
  canNote,
  saving,
  named = false,
}: {
  title: string;
  note: string;
  rows: ContactRow[];
  onOpen: (ondoId: string, name: string) => void;
  onSaveNote: (subject: NoteSubject, body: string, tags: string[]) => void;
  onClearNote: (noteId: string) => void;
  onRemoveAdded: (addedId: string) => void;
  /** Editor rung. See `Contacts`. */
  canNote: boolean;
  saving: string | null;
  named?: boolean;
}) {
  if (rows.length === 0) return null;

  return (
    // NOT `.gallery__group`, which is a flex header ROW that a settlement list
    // puts a title and a count on opposite ends of. This is a container.
    <section className="gallery__contacts-section">
      <h2 className="gallery__section-title">
        {title} — {rows.length}
      </h2>
      <p className="gallery__section-note">{note}</p>

      <ul className="gallery__contacts">
        <li className="gallery__contacts-head" aria-hidden="true">
          <span />
          <span>{CONTACTS_COPY.columns.contact}</span>
          <span>{CONTACTS_COPY.columns.type}</span>
          <span>{CONTACTS_COPY.columns.location}</span>
          <span className="gallery__contacts-head-num">
            {CONTACTS_COPY.columns.heldNow}
          </span>
          <span className="gallery__contacts-head-num">
            {CONTACTS_COPY.columns.historic}
          </span>
          <span className="gallery__contacts-head-num">
            {CONTACTS_COPY.columns.verified}
          </span>
          <span className="gallery__contacts-head-num">
            {CONTACTS_COPY.columns.revenue}
          </span>
          <span className="gallery__contacts-head-num">
            {CONTACTS_COPY.columns.last}
          </span>
        </li>
        {rows.map((row) => (
          <Row
            key={row.key}
            row={row}
            onOpen={onOpen}
            onSaveNote={onSaveNote}
            onClearNote={onClearNote}
            onRemoveAdded={onRemoveAdded}
            canNote={canNote}
            saving={saving}
            named={named}
          />
        ))}
      </ul>
    </section>
  );
}

/**
 * One contact.
 *
 * A NAMED CONTACT IS NOT CLICKABLE, and that is the disclosure rule showing
 * through the interface rather than a missing feature: the timeline is keyed by
 * ONDO id, and a name resolves to nobody. A row that looked openable and did
 * nothing would imply the registry knows something about it.
 */
function Row({
  row,
  onOpen,
  onSaveNote,
  onClearNote,
  onRemoveAdded,
  canNote,
  saving,
  named,
}: {
  row: ContactRow;
  onOpen: (ondoId: string, name: string) => void;
  onSaveNote: (subject: NoteSubject, body: string, tags: string[]) => void;
  onClearNote: (noteId: string) => void;
  onRemoveAdded: (addedId: string) => void;
  /** Editor rung. See `Contacts`. */
  canNote: boolean;
  saving: string | null;
  named: boolean;
}) {
  const body = (
    <>
      {named ? (
        <InitialsTile initials={row.initials} className="gallery__contact-initials--named" />
      ) : (
        <InitialsTile initials={row.initials} />
      )}
      <span className="gallery__contact-body">
        <span className="gallery__contact-name">
          {row.name}
          {row.live ? null : <Tag tone="quiet">Past</Tag>}
        </span>
        <span className="gallery__contact-meta">{row.sub}</span>
      </span>
      <span className="gallery__contact-rel">{row.type}</span>
      <span className="gallery__contact-rel">{row.location}</span>
      <span className="gallery__contact-works">{row.heldNow}</span>
      <span className="gallery__contact-works">{row.historic}</span>
      <span className="gallery__contact-works">
        {row.verified ? (
          <Tag tone="verified">{CONTACTS_COPY.verifiedMark}</Tag>
        ) : (
          CONTACTS_COPY.spendNone
        )}
      </span>
      <span className="gallery__contact-spend">{row.spend}</span>
      <span className="gallery__contact-when">{row.last}</span>
    </>
  );

  return (
    <li className="gallery__contact">
      {row.ondoId ? (
        <button
          type="button"
          className="gallery__contact-open"
          onClick={() => onOpen(row.ondoId as string, row.name)}
        >
          {body}
        </button>
      ) : (
        <span className="gallery__contact-open gallery__contact-open--static">{body}</span>
      )}
      {/*
        KEYED ON THE NOTE IT CAME FROM, which is what lets `Note` hold its draft
        in plain `useState` with no effect to reset it. A save refetches the
        list; if the note came back changed the key changes, React remounts, and
        the box starts from the new value. Resetting it in an effect instead
        cascades a render and leaves a window where the draft is stale.
      */}
      {canNote ? (
        <Note
          key={`${row.noteId ?? "none"}:${row.note ?? ""}`}
          row={row}
          onSave={onSaveNote}
          onClear={onClearNote}
          saving={saving}
        />
      ) : (
        // A note already written is still shown — it is the firm's record and
        // every seat may read it. Only writing one needs the rung.
        row.note && <p className="gallery__contact-note">{row.note}</p>
      )}
      {/* Retracting an add is the one per-row write beside the note, and only
          on rows that WERE added — a derived contact has nothing to retract. */}
      {canNote && row.addedId ? (
        <div className="gallery__note-line">
          <button
            type="button"
            className="gallery__note-edit"
            disabled={saving !== null}
            onClick={() => onRemoveAdded(row.addedId as string)}
          >
            {CONTACTS_COPY.addedRemove}
          </button>
        </div>
      ) : null}
    </li>
  );
}

/**
 * The firm's own note on one contact.
 *
 * COLLAPSED UNTIL THERE IS SOMETHING TO SAY, because a textarea against every
 * row would make the screen look like a form when it is a ledger. A contact
 * carrying a note shows it; one that does not shows a control to add one.
 *
 * THE WARNING IS PART OF THE FEATURE, not decoration. This is the only place in
 * ONDO where a principal writes something about another principal that its
 * subject can never see or answer, and a reader should be told that while they
 * type rather than have to infer it.
 */
function Note({
  row,
  onSave,
  onClear,
  saving,
}: {
  row: ContactRow;
  onSave: (subject: NoteSubject, body: string, tags: string[]) => void;
  onClear: (noteId: string) => void;
  saving: string | null;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(row.note ?? "");
  const [tags, setTags] = useState(row.tags.join(", "));
  const fieldId = useId();

  const busy = saving !== null;

  if (!editing) {
    return (
      <div className="gallery__note-line">
        {row.note ? <p className="gallery__note-body">{row.note}</p> : null}
        {row.tags.length > 0 ? (
          <span className="gallery__note-tags">
            {row.tags.map((tag) => (
              <Tag key={tag} tone="neutral">
                {tag}
              </Tag>
            ))}
          </span>
        ) : null}
        <button
          type="button"
          className="gallery__note-edit"
          onClick={() => setEditing(true)}
        >
          {row.note ? CONTACTS_COPY.noteEdit : CONTACTS_COPY.noteAdd}
        </button>
        {row.noteId ? (
          <button
            type="button"
            className="gallery__note-edit"
            disabled={busy}
            onClick={() => onClear(row.noteId as string)}
          >
            {CONTACTS_COPY.noteRemove}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <form
      className="gallery__note-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSave(
          row.subject,
          draft,
          tags
            .split(",")
            .map((tag) => tag.trim())
            .filter((tag) => tag.length > 0),
        );
      }}
    >
      <label className="gallery__note-label" htmlFor={`${fieldId}-body`}>
        {CONTACTS_COPY.noteLabel}
      </label>
      <textarea
        id={`${fieldId}-body`}
        className="gallery__note-input"
        rows={2}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder={CONTACTS_COPY.notePlaceholder}
      />
      <label className="gallery__note-label" htmlFor={`${fieldId}-tags`}>
        {CONTACTS_COPY.tagsLabel}
      </label>
      <input
        id={`${fieldId}-tags`}
        className="gallery__note-input"
        value={tags}
        onChange={(event) => setTags(event.target.value)}
        placeholder={CONTACTS_COPY.tagsPlaceholder}
      />
      <p className="gallery__note-warn">{CONTACTS_COPY.notePrivate}</p>
      <div className="gallery__note-actions">
        <button type="submit" className="gallery__note-save" disabled={busy}>
          {CONTACTS_COPY.noteSave}
        </button>
        <button
          type="button"
          className="gallery__note-edit"
          onClick={() => setEditing(false)}
        >
          {CONTACTS_COPY.noteCancel}
        </button>
      </div>
    </form>
  );
}

/** What the two of you have done together. Opened from a row, never routed to. */
function Timeline({
  timeline,
  onClose,
}: {
  timeline: TimelineView;
  onClose: () => void;
}) {
  if (timeline.kind === "closed") return null;

  return (
    <section className="gallery__drawer" aria-label={timeline.name}>
      <div className="gallery__drawer-head">
        <h2 className="gallery__section-title">{timeline.name}</h2>
        <button type="button" className="gallery__drawer-close" onClick={onClose}>
          {CONTACTS_COPY.close}
        </button>
      </div>

      {timeline.kind === "loading" ? (
        <p className="gallery__note" role="status">
          {CONTACTS_COPY.loading}
        </p>
      ) : timeline.kind === "error" ? (
        <p className="gallery__note gallery__note--error" role="alert">
          {timeline.message}
        </p>
      ) : timeline.empty ? (
        <p className="gallery__note">{timeline.empty}</p>
      ) : (
        <ul className="gallery__timeline">
          {timeline.rows.map((row) => (
            <li key={row.key} className="gallery__timeline-row">
              {/*
                THE ONE IMAGE ON THIS SCREEN, and it is not a signed storage
                URL. The bytes arrived from the API with this reader's name
                composited into them, so there is no request from here that
                could return the unstamped original. A row with no picture —
                an affiliation, a work with no photograph, bytes storage
                refused — renders the placeholder.
              */}
              <WorkThumb imageUrl={row.imageUrl} title={row.title ?? row.what} />
              <span className="gallery__timeline-when">{row.when}</span>
              <span className="gallery__timeline-what">{row.what}</span>
              <span className="gallery__timeline-work">
                {row.href && row.title ? (
                  <Link to={row.href}>{row.title}</Link>
                ) : (
                  row.title
                )}
              </span>
              {row.live ? <Tag tone="pending">Open</Tag> : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
